import React, { use, useEffect, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { HeaderThElement } from '@progress/kendo-react-data-tools';
import { GridCustomHeaderCellProps } from '@progress/kendo-react-grid';
import { ScoreDirection } from '@components-clean/central-monitoring/panels/panel-constants';
import Card from '@components-clean/core/card';
import ErrorMessage from '@components-clean/core/error';
import Grid from '@components-clean/core/grid-fe';
import { MultiValueDataCell, PanelWeightCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import Loading from '@components-clean/core/loading/loading';
import { useStudyDataFilterContext } from '@contexts-clean/study-data-filter-context';
import { StudyOverviewData } from '@interfaces/central-monitoring';
import { StudySiteHubContext } from '@pages-clean/central-monitoring/interfaces/context';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { useLocalState, useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { GET_STUDY_HUB_STUDY_FIELDS_CONFIG } from '@queries/central-monitoring';
import {
    getStudyHubStudyFieldsConfig,
    getStudyHubStudyFieldsConfigVariables,
} from '@queries/generated/central-monitoring.generated';
import StudyHubApi from '@apis/bff-api/central-monitoring/study-hub';

interface IComparison {
    valueInFocus?: number | null;
    comparisonValue?: number | null;
    changeDirection?: ScoreDirection;
}

interface IPanelWeightDetails {
    includeInSiteScoring?: boolean | null;
    weight?: number | null;
}

interface IStudyOverviewComparisonData {
    reference: string;
    plotTitle: string;
    panelWeightDetails?: IPanelWeightDetails;
    redCount: IComparison | number | null;
    redDelta?: number | null;
    amberCount: IComparison | number | null;
    amberDelta?: number | null;
    greenCount: IComparison | number | null;
    greenDelta?: number | null;
    studyMean?: IComparison | number | null;
    studyMeanDelta?: number | null;
    studyRate?: IComparison | number | null;
    studyRateDelta?: number | null;
    countryMean?: IComparison | number | null;
    countryMeanDelta?: number | null;
    siteRate?: IComparison | number | null;
    siteRateDelta?: number | null;
    yLabel: string | null | undefined;
    yValue?: IComparison | number | null;
    yDelta?: number | null;
    xLabel: string;
    xValue?: IComparison | number | null;
    xDelta?: number | null;
}

const StudyView = () => {
    const { t }: { t: (key: string) => string } = useTranslation('cm', {
        keyPrefix: 'studyHub.studyOverviewTable',
    });
    const { current } = use(StudySiteHubContext);
    const { studyRef } = current;
    const datasetId = current.dataset?.id;
    const datasetDate = stringDateWithoutTimeFromUtc(current.dataset?.date, 'DD-MMM-YYYY')!;
    const comparisonDatasetId = current.datasetFilters.comparisonDataset?.id ?? undefined;
    const comparisonDatasetDate = stringDateWithoutTimeFromUtc(
        current.datasetFilters.comparisonDataset?.dataCutDate ?? null,
        'DD-MMM-YYYY',
    )!;
    const { state: localState } = useLocalState();
    const studyName = localState?.selectedStudy?.studyNumber;
    const { sessionId: siteFilterSessionId, filtersChanged$ } = useStudyDataFilterContext();
    const {
        data,
        error,
        isMutating: isLoading = true,
        trigger,
    } = StudyHubApi.useGet(studyRef, siteFilterSessionId, datasetId, comparisonDatasetId);

    const [loadConfig, { data: studyFieldsConfigData }] = useLazyQuery<
        getStudyHubStudyFieldsConfig,
        getStudyHubStudyFieldsConfigVariables
    >(GET_STUDY_HUB_STUDY_FIELDS_CONFIG);

    useEffect(() => {
        loadConfig({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [studyRef]);

    useEffect(() => {
        const triggerRequest = () => {
            if (!siteFilterSessionId || !studyFieldsConfigData) {
                return;
            }
            trigger().catch((err) => window.console.log(err));
        };
        triggerRequest();

        // Subscribe to the filter changed event emitted by the Filter UI, and re-trigger the get
        const subscription = filtersChanged$.subscribe(() => {
            triggerRequest();
        });

        // Return a function that unsubscribes from the observable
        return () => subscription.unsubscribe();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [studyRef, datasetId, siteFilterSessionId, comparisonDatasetId, studyFieldsConfigData]);

    interface ITableInfo {
        columns: GridColumnProps[];
        data: StudyOverviewData[] | IStudyOverviewComparisonData[];
    }

    const getTableColumn = (
        valueField: string,
        deltaField: string,
        translationKey: string,
        emptyDisplayText?: string,
        isNumeric?: boolean,
        isRounded?: boolean,
        ignoreGrouping: boolean = false,
    ) => {
        const columns: GridColumnProps[] = [
            {
                id: `${valueField}_values`,
                field: valueField,
                title: comparisonDatasetId !== undefined ? t('groupTitles.values') : t(translationKey),
                customColumn: true,
                width: comparisonDatasetId !== undefined ? 160 : undefined,
                minWidth: comparisonDatasetId !== undefined ? 160 : 110,
                cells: {
                    data:
                        comparisonDatasetId !== undefined
                            ? (gridCellProps) =>
                                  MultiValueDataCell({
                                      ...gridCellProps,
                                      numberFormat: isRounded ? '0.00' : undefined,
                                  })
                            : undefined,
                },
                filter: comparisonDatasetId !== undefined || isNumeric ? 'numeric' : undefined,
                format: isRounded ? '{0:0.00}' : undefined,
                emptyDisplayText: emptyDisplayText ?? undefined,
                sortable: true,
                filterKey: comparisonDatasetId !== undefined ? 'valueInFocus' : undefined,
                comparisonFilterKey: comparisonDatasetId !== undefined ? 'comparisonValue' : undefined,
                multiValueTypeColumn: comparisonDatasetId !== undefined,
                multiValueExportTitles:
                    comparisonDatasetId !== undefined
                        ? {
                              comparisonValue: comparisonDatasetDate,
                              valueInFocus: datasetDate,
                              changeDirection: t('exportColumns.changeDirection'),
                          }
                        : undefined,
            },
            {
                id: `${deltaField}_change`,
                field: deltaField,
                title: t('groupTitles.change'),
                minWidth: 90,
                customColumn: true,
                multiValueTypeColumn: comparisonDatasetId !== undefined,
                show: comparisonDatasetId !== undefined,
                filter: 'numeric',
                format: isRounded ? '{0:0.00}' : undefined,
            },
        ];
        return comparisonDatasetId !== undefined && !ignoreGrouping
            ? [
                  {
                      id: `${valueField}_group`,
                      title: t(translationKey),
                      locked: false,
                      children: columns,
                      sortable: false,
                  },
              ]
            : columns;
    };

    const tableInfo = useMemo(() => {
        if (!data) return undefined;

        const showSiteRate = data.some((res) => res.siteRate !== null || res.siteRateComparison !== null);
        const showCountrySiteMean = data.some((res) => res.countryMean !== null || res.countryMeanComparison !== null);

        let processedData;
        if (comparisonDatasetId) {
            const flattenedData: IStudyOverviewComparisonData[] = [];
            data.forEach((panel: StudyOverviewData) => {
                flattenedData.push({
                    reference: panel.reference,
                    plotTitle: panel.plotTitle,
                    panelWeightDetails: {
                        includeInSiteScoring: panel.includeInSiteScoring,
                        weight: panel.weight,
                    },
                    redCount: {
                        valueInFocus: panel.redCount,
                        comparisonValue: panel.redCountComparison,
                        changeDirection: panel.redCountChangeDirection,
                    },
                    // Changes between column
                    redDelta: panel.redDelta,
                    amberCount: {
                        valueInFocus: panel.amberCount, // Current value
                        comparisonValue: panel.amberCountComparison, // previous value
                        changeDirection: panel.amberCountChangeDirection,
                    },
                    amberDelta: panel.amberDelta,
                    greenCount: {
                        valueInFocus: panel.greenCount,
                        comparisonValue: panel.greenCountComparison,
                        changeDirection: panel.greenCountChangeDirection,
                    },
                    greenDelta: panel.greenDelta,
                    studyMean: {
                        valueInFocus: panel.studyMean,
                        comparisonValue: panel.studyMeanComparison,
                        changeDirection: panel.studyMeanChangeDirection,
                    },
                    studyMeanDelta: panel.studyMeanDelta,
                    studyRate: {
                        valueInFocus: panel.studyRate,
                        comparisonValue: panel.studyRateComparison,
                        changeDirection: panel.studyRateChangeDirection,
                    },
                    studyRateDelta: panel.studyRateDelta,
                    countryMean: {
                        valueInFocus: panel.countryMean,
                        comparisonValue: panel.countryMeanComparison,
                        changeDirection: panel.countryMeanChangeDirection,
                    },
                    countryMeanDelta: panel.countryMeanDelta,
                    siteRate: {
                        valueInFocus: panel.siteRate,
                        comparisonValue: panel.siteRateComparison,
                        changeDirection: panel.siteRateMeanChangeDirection,
                    },
                    siteRateDelta: panel.siteRateDelta,
                    yLabel: panel.yLabel,
                    yValue: {
                        valueInFocus: panel.yValue,
                        comparisonValue: panel.yValueComparison,
                        changeDirection: panel.yChangeDirection,
                    },
                    yDelta: panel.yDelta,
                    xLabel: panel.xLabel,
                    xValue: {
                        valueInFocus: panel.xValue,
                        comparisonValue: panel.xValueComparison,
                        changeDirection: panel.xChangeDirection,
                    },
                    xDelta: panel.xDelta,
                });
            });
            processedData = flattenedData;
        } else {
            const flattenedData: IStudyOverviewComparisonData[] = [];
            data.forEach((panel: StudyOverviewData) => {
                flattenedData.push({
                    reference: panel.reference,
                    plotTitle: panel.plotTitle,
                    panelWeightDetails: {
                        includeInSiteScoring: panel.includeInSiteScoring,
                        weight: panel.includeInSiteScoring ? panel.weight : undefined,
                    },
                    redCount: panel.redCount,
                    amberCount: panel.amberCount,
                    greenCount: panel.greenCount,
                    studyMean: panel.studyMean,
                    studyRate: panel.studyRate,
                    countryMean: panel.countryMean,
                    siteRate: panel.siteRate,
                    yLabel: panel.yLabel,
                    yValue: panel.yValue,
                    xLabel: panel.xLabel,
                    xValue: panel.xValue,
                });
            });
            processedData = flattenedData;
        }

        const info: ITableInfo = {
            columns: [
                {
                    id: 'panelWeightDetails',
                    cells: {
                        headerCell: (props: GridCustomHeaderCellProps) => {
                            return (
                                <HeaderThElement
                                    columnId={props.thProps?.columnId || ''}
                                    {...props.thProps}
                                    data-multi-column={false}
                                >
                                    <span style={{ visibility: 'hidden' }}>{props.children}</span>
                                </HeaderThElement>
                            );
                        },
                        data: PanelWeightCell,
                    },
                    // This prop needs to be defined for the Excel export.
                    title: 'Weight if Included in Scoring',
                    // This prop need to defined for sorting to work correctly.
                    field: 'panelWeightDetails.weight',
                    customColumn: true,
                    width: 50,
                    filterable: false,
                    resizable: false,
                    locked: true,
                },
                {
                    id: 'plotTitle',
                    field: 'plotTitle',
                    title: t('columns.panel'),
                    width: 1,
                    minWidth: 140,
                    locked: true,
                },
            ],
            data: processedData,
        };

        if (studyFieldsConfigData) {
            if (studyFieldsConfigData.getStudyHubStudyFieldsConfig?.numGreenSites)
                info.columns.push(...getTableColumn('greenCount', 'greenDelta', 'columns.greenSites', undefined, true));

            if (studyFieldsConfigData.getStudyHubStudyFieldsConfig?.numAmberSites)
                info.columns.push(...getTableColumn('amberCount', 'amberDelta', 'columns.amberSites', undefined, true));

            if (studyFieldsConfigData.getStudyHubStudyFieldsConfig?.numRedSites)
                info.columns.push(...getTableColumn('redCount', 'redDelta', 'columns.redSites', '0', true));

            if (studyFieldsConfigData.getStudyHubStudyFieldsConfig?.studySiteMean)
                info.columns.push(
                    ...getTableColumn('studyMean', 'studyMeanDelta', 'columns.siteMean', undefined, true, true),
                );

            if (studyFieldsConfigData.getStudyHubStudyFieldsConfig?.countrySiteMean && showCountrySiteMean)
                info.columns.push(
                    ...getTableColumn('countryMean', 'countryMeanDelta', 'columns.countryMean', 'N/A', true, true),
                );

            if (studyFieldsConfigData.getStudyHubStudyFieldsConfig?.studyRate)
                info.columns.push(
                    ...getTableColumn('studyRate', 'studyRateDelta', 'columns.studyRate', 'N/A', true, true),
                );

            if (studyFieldsConfigData.getStudyHubStudyFieldsConfig?.siteRate && showSiteRate)
                info.columns.push(
                    ...getTableColumn('siteRate', 'siteRateDelta', 'columns.siteRate', 'N/A', true, true),
                );
        }

        info.columns.push(
            {
                id: 'yVolume',
                field: 'yVolume',
                title: t('columns.yLabelTitle'),
                width: 1,
                minWidth: 140,
                children: [
                    {
                        id: 'yLabel',
                        field: 'yLabel',
                        title: t('columns.yLabel'),
                        width: 1,
                        minWidth: 140,
                    },
                    ...(comparisonDatasetId !== undefined
                        ? getTableColumn('yValue', 'yDelta', 'columns.yValue', undefined, true, undefined, true)
                        : [getTableColumn('yValue', 'yDelta', 'columns.yValue', undefined, true, undefined, true)[0]]),
                ],
            },
            {
                id: 'xVolume',
                field: 'xVolume',
                title: t('columns.xLabelTitle'),
                width: 1,
                minWidth: 140,
                children: [
                    {
                        id: 'xLabel',
                        field: 'xLabel',
                        title: t('columns.xLabel'),
                        width: 1,
                        minWidth: 140,
                    },
                    ...(comparisonDatasetId !== undefined
                        ? getTableColumn('xValue', 'xDelta', 'columns.xValue', undefined, true, undefined, true)
                        : [getTableColumn('xValue', 'xDelta', 'columns.xValue', undefined, true, undefined, true)[0]]),
                ],
            },
        );
        return info;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [data]);

    if (!data || isLoading || error) {
        return (
            <Card data-testid="study-view-loading-card" title={t('title')}>
                {error ? <ErrorMessage /> : <Loading />}
            </Card>
        );
    }

    const exportFileName = comparisonDatasetDate
        ? `Study Grid_${studyName}_Dataset(s) ${comparisonDatasetDate} - ${datasetDate}`
        : `Study Grid_${studyName}_Dataset(s) ${datasetDate}`;

    return (
        <Grid
            title={t('title')}
            maxHeight="auto"
            testDataId="study-view-grid"
            data={tableInfo?.data ?? []}
            columns={tableInfo?.columns ?? []}
            loading={isLoading}
            useLoadingOverlay
            filterable={false}
            sortable
            reorderable={false}
            exportFilename={exportFileName}
            gridSettingsMenuDisabled
            showAddIcon={false}
            service={AuditServiceType.CENTRAL_MONITORING_2}
            area={AuditApplicationArea.CENTRAL_MONITORING_2}
            dataType={AuditDataType.EXCEL_EXPORT} // The data doesn't have an OPRA type so just use this
            auditDescriptionPrefix="CM - Study Hub"
        />
    );
};

export default StudyView;
